/** @format */

import { Extension } from "@tiptap/core";
import type { Editor } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

export interface FeedbackHighlight {
  from: number;
  to: number;
  feedbackId: string;
  selectedText?: string | null;
}

const feedbackHighlightKey = new PluginKey<DecorationSet>("feedbackHighlight");

export const FeedbackHighlightExtension = Extension.create({
  name: "feedbackHighlight",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: feedbackHighlightKey,
        state: {
          init() {
            return DecorationSet.empty;
          },
          apply(tr, oldSet) {
            const highlights = tr.getMeta(feedbackHighlightKey);
            if (highlights !== undefined) {
              return highlights;
            }
            return oldSet.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return feedbackHighlightKey.getState(state);
          },
        },
      }),
    ];
  },
});

/** Returns the set of feedbackIds that overlap with at least one other highlight. */
export function computeOverlappingIds(highlights: FeedbackHighlight[]): Set<string> {
  const overlapping = new Set<string>();
  for (let i = 0; i < highlights.length; i++) {
    for (let j = i + 1; j < highlights.length; j++) {
      if (highlights[i].from < highlights[j].to && highlights[j].from < highlights[i].to) {
        overlapping.add(highlights[i].feedbackId);
        overlapping.add(highlights[j].feedbackId);
      }
    }
  }
  return overlapping;
}

/** Returns all feedback IDs whose range contains the given document position. */
export function getFeedbackIdsAtPos(highlights: FeedbackHighlight[], pos: number): string[] {
  return highlights
    .filter((h) => h.from <= pos && pos < h.to)
    .map((h) => h.feedbackId);
}

export function setFeedbackHighlights(
  editor: Editor,
  highlights: FeedbackHighlight[],
  selectedFeedbackId?: string | null,
) {
  const { state, view } = editor;
  const docSize = state.doc.content.size;
  const overlapping = computeOverlappingIds(highlights);

  const decorations = highlights
    .filter((h) => h.from < docSize)
    .map((h) => {
      const clampedTo = Math.min(h.to, docSize);
      const isSelected = selectedFeedbackId != null && h.feedbackId === selectedFeedbackId;
      const isStacked = overlapping.has(h.feedbackId);
      const cls = [
        "feedback-highlight",
        isStacked ? "feedback-highlight--stacked" : "",
        isSelected ? "feedback-highlight--selected" : "",
      ]
        .filter(Boolean)
        .join(" ");
      return Decoration.inline(h.from, clampedTo, {
        class: cls,
        "data-feedback-id": h.feedbackId,
      });
    });

  const set = DecorationSet.create(state.doc, decorations);
  const tr = state.tr.setMeta(feedbackHighlightKey, set);
  view.dispatch(tr);
}
